import { Inject, Injectable, Optional, PLATFORM_ID, makeStateKey, TransferState } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import truncate from 'lodash/truncate';
import { BehaviorSubject } from 'rxjs';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '@shared/tokens/server.tokens';
import { TranslateService } from '@ngx-translate/core';
import { LocationManagerService } from '@cakeiteasy/web-utils';

export const SSR_BASED_META = makeStateKey<boolean>('SSR_BASED_META');

export enum JSONLD_TYPES {
  CIE,
  BREADCRUMBS,
  PAGE_SPECIFIC
}

export interface IJsonLdData {
  key: JSONLD_TYPES;
  json: any;
}

@Injectable()
export class PageMetaService {
  public jsonLds$ = new BehaviorSubject<IJsonLdData[]>([]);

  public code = {
    no: 'no',
    se: 'se'
  };

  public domainName: string;

  constructor(private _meta: Meta,
              private _title: Title,
              private _transferState: TransferState,
              private _translationService: TranslateService,
              private _locationService: LocationManagerService,
              @Inject('AppConfig') private _appConfig,
              @Inject(DOCUMENT) private doc,
              @Inject(PLATFORM_ID) private _platformId: Object,
              @Optional() @Inject(REQUEST) private _request: any) {
    this.domainName = this._locationService.getDomainName();
    this._setCieJsonLd();
  }

  public isBrowserRenderingAfterSSR() {
    return isPlatformBrowser(this._platformId) && this._transferState.get(SSR_BASED_META, false);
  }

  public setNoIndex() {
    this.setTag('robots', 'noindex');
  }

  public applyRouteMeta(settings: { title?: string; description?: string; image?: string } = {}): void {
    this.set(settings.title || 'pageMeta.default.title', settings.description || '', settings.image);
  }

  public setTag(name: string, content: string): void {
    const isProperty = name.startsWith('og:');
    const attribute = isProperty ? 'property' : 'name';
    this._meta.updateTag({ [attribute]: name, content }, `${attribute}='${name}'`);
  }

  public set(title: string = '', description: string = '', imgUrl?: string, locale?: string,
             appendSiteName: boolean = true, ogUrl: string = '') {
    if (this.isBrowserRenderingAfterSSR()) {
      this._transferState.set(SSR_BASED_META, false);
      return;
    }

    description = description ? (this._translationService.instant(description) || description) : '';
    const truncatedDesc = truncate(description, { length: 180 });
    imgUrl = imgUrl || 'https://cdn.cakeiteasy.com/marketplace/FB-Share-Business.jpg';

    title = title ? (this._translationService.instant(title) || title) : '';
    title += (appendSiteName ? (' | ' + this._translationService.instant('references.marketplaceName')) : '');

    this._title.setTitle(title);
    this.setTag('image', imgUrl);

    this.setTag('og:image', imgUrl);
    this.setTag('og:title', title);

    if (truncatedDesc) {
      this.setTag('description', truncatedDesc);
      this.setTag('og:description', truncatedDesc);
    }

    this.setTag('og:url', ogUrl || this._getCurrentHref());

    if (locale) {
      this._setLocale(locale);
    }

    if (isPlatformServer(this._platformId)) {
      this._transferState.set(SSR_BASED_META, true);
    }
  }

  public updateCanonicalUrl() {
    try {
      const url = this._getCurrentHref();

      const prevLink = this.doc.head.querySelector('[rel="canonical"]');
      if (prevLink) {
        return prevLink.setAttribute('href', url);
      }

      const link: HTMLLinkElement = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
      link.setAttribute('href', url);
    } catch (err) {
      console.error(err);
    }
  }

  public setPageJsonLd(json: any = null, breadcrumbs: any[] = null): void {
    this._setJsonLd(JSONLD_TYPES.PAGE_SPECIFIC, json);

    if (breadcrumbs && breadcrumbs.length) {
      const crumbsJson = {
        '@type': 'BreadcrumbList',
        '@id': this._getCurrentHref(),
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'item': {
              '@type': 'WebPage',
              '@id': `https://www.cakeiteasy.${this.domainName}`,
              'url': `https://www.cakeiteasy.${this.domainName}`,
              'name': 'Home'
            }
          },
          ...breadcrumbs
        ]
      };

      this._setJsonLd(JSONLD_TYPES.BREADCRUMBS, crumbsJson);
    } else {
      this._setJsonLd(JSONLD_TYPES.BREADCRUMBS, null);
    }
  }

  private _setLocale(locale: string) {
    this.setTag('og:locale', locale);
  }

  private _setJsonLd(key: number, json: any) {
    const prevValue = this.jsonLds$.value;
    let index = prevValue.findIndex((i) => i.key === key);
    if (index === -1) {
      index = prevValue.length;
      prevValue[index] = { key, json: json };
    } else {
      prevValue[index].json = json;
    }

    this.jsonLds$.next([...prevValue]);
  }

  private _getCurrentHref(hostOnly: boolean = false) {
    let href = '';

    if (isPlatformBrowser(this._platformId)) {
      href = window.location.href.split('?')[0];
    } else if (this._request) {
      let { path } = this._request;
      const { headers, baseUrl } = this._request;

      const protocol = this._appConfig.production ? 'https' : 'http';

      if (hostOnly) {
        path = '';
      }

      href = `${protocol}://${headers['origin'] || headers['host']}${baseUrl}${path}/`.replace('//', '/');
    }
    return href;
  }

  private _setCieJsonLd() {
    const host = 'https://' + this._locationService.getHost();
    const norway = {
      name: 'Norway',
      telephone: '+47 22 33 26 00',
      language: {
        '@type': 'Language',
        name: 'Norwegian',
        alternateName: 'no'
      },
    };
    const sweden = {
      name: 'Sweden',
      telephone: '+46 10 205 00 13',
      language: {
        '@type': 'Language',
        name: 'Swedish',
        alternateName: 'sv'
      }
    };

    const languages = [
      this.domainName === this.code.no ? norway.language : sweden.language,
      {
        '@type': 'Language',
        name: 'English',
        alternateName: 'en'
      }
    ];

    const json = {
      '@type': 'Organization',
      'name': 'Cake it easy',
      'url': `https://www.cakeiteasy.${this.domainName}`,
      'logo': host + '/assets/img/logo.png',
      'sameAs': [`https://www.facebook.com/cakeiteasy.${this.domainName}`],
      'contactPoint': {
        '@type': 'ContactPoint',
        'telephone': this.domainName === this.code.no ? norway.telephone : sweden.telephone,
        'contactType': 'Customer service',
        'availableLanguage': languages,
        'areaServed': {
          'type': 'country',
          'name': this.domainName === this.code.no ? norway.name : sweden.name,
        }
      }
    };

    if (this.domainName === this.code.no) {
      json['sameAs'].push('https://www.trustpilot.com/review/cakeiteasy.no');
    }

    this._setJsonLd(JSONLD_TYPES.CIE, json);
  }

}
